import React, { useId } from 'react';
import { Box, Chip, CircularProgress, Tooltip } from '@mui/material';
import { FiberManualRecord, WbTwilight } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';

export type SaleTagValue = 'Live' | 'Sunset';

// Stored values from before the Active -> Live rename may still say "Active".
export const normalizeSaleTagValue = (value?: string | null): SaleTagValue | undefined =>
    value === 'Live' || value === 'Active' ? 'Live' : value === 'Sunset' ? 'Sunset' : undefined;

const TONES: Record<SaleTagValue, { color: string; bg: string }> = {
    Live: { color: '#15803d', bg: 'rgba(34, 197, 94, 0.16)' },
    Sunset: { color: '#b91c1c', bg: 'rgba(239, 68, 68, 0.14)' },
};

const OPTIONS: { value: SaleTagValue; icon: React.ReactNode }[] = [
    { value: 'Live', icon: <FiberManualRecord sx={{ fontSize: 9 }} /> },
    { value: 'Sunset', icon: <WbTwilight sx={{ fontSize: 13 }} /> },
];

interface SaleTagToggleProps {
    value?: string | null;
    canEdit: boolean;
    loading?: boolean;
    onChange: (next: SaleTagValue) => void;
}

// Parent-level Sale Tag. Editors get a two-segment toggle whose indicator slides between
// Live and Sunset; everyone else sees a read-only chip. An unset parent shows both
// segments un-highlighted so it is visibly "not chosen yet".
const SaleTagToggle: React.FC<SaleTagToggleProps> = ({ value, canEdit, loading = false, onChange }) => {
    const current = normalizeSaleTagValue(value);
    const layoutId = useId();
    const reduceMotion = useReducedMotion();

    if (!canEdit) {
        if (!current) return null;
        return (
            <Chip
                size="small"
                icon={current === 'Live' ? <FiberManualRecord sx={{ fontSize: '9px !important' }} /> : <WbTwilight sx={{ fontSize: '13px !important' }} />}
                label={`Sale Tag: ${current}`}
                sx={{ height: 20, fontWeight: 700, fontSize: '0.62rem', bgcolor: TONES[current].bg, color: TONES[current].color, '& .MuiChip-icon': { color: 'inherit', ml: 0.6 }, '& .MuiChip-label': { px: 0.75 } }}
            />
        );
    }

    return (
        <Box
            role="radiogroup"
            aria-label="Sale Tag"
            aria-busy={loading}
            onClick={(e) => e.stopPropagation()}
            sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                p: '2px',
                borderRadius: '999px',
                bgcolor: 'rgba(26, 20, 15, 0.06)',
                border: '1px solid rgba(26, 20, 15, 0.08)',
                opacity: loading ? 0.75 : 1,
            }}
        >
            {OPTIONS.map(({ value: option, icon }) => {
                const selected = current === option;
                return (
                    <Tooltip key={option} title={selected ? `Sale Tag is ${option}` : `Set Sale Tag to ${option} for all versions`} enterDelay={400}>
                        <Box
                            component="button"
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            aria-label={`Sale Tag ${option}`}
                            disabled={loading}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                if (!selected) onChange(option);
                            }}
                            sx={{
                                position: 'relative',
                                zIndex: 1,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.4,
                                minWidth: 58,
                                height: 24,
                                px: 1,
                                justifyContent: 'center',
                                border: 0,
                                bgcolor: 'transparent',
                                borderRadius: '999px',
                                font: 'inherit',
                                fontSize: '0.66rem',
                                fontWeight: 700,
                                letterSpacing: 0.2,
                                cursor: loading ? 'progress' : selected ? 'default' : 'pointer',
                                color: selected ? TONES[option].color : 'rgba(26, 20, 15, 0.62)',
                                transition: 'color 160ms ease',
                                '&:hover': { color: selected ? TONES[option].color : TONES[option].color },
                                '&:focus-visible': { outline: '2px solid #DC5829', outlineOffset: 1 },
                            }}
                        >
                            {selected && (
                                <motion.span
                                    layoutId={layoutId}
                                    transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 520, damping: 38 }}
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        borderRadius: 999,
                                        background: TONES[option].bg,
                                        boxShadow: `inset 0 0 0 1px ${TONES[option].color}33`,
                                        zIndex: -1,
                                    }}
                                />
                            )}
                            {loading && selected ? <CircularProgress size={10} thickness={6} sx={{ color: 'inherit' }} /> : icon}
                            {option}
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
};

export default SaleTagToggle;
